import { Divider } from 'tradera/components/divider/divider';
import {
    DropdownButton,
    type DropdownButtonProps
} from 'tradera/components/dropdown-button/dropdown-button';

import styles from './dropdown-menu2.module.scss';
import type React from 'react';
import {
    forwardRef,
    useCallback,
    useEffect,
    useState,
    useContext,
    useRef,
    createContext,
    Children,
    cloneElement,
    isValidElement
} from 'react';
import type { ReactNode, RefObject } from 'react';
import {
    BottomSheet,
    type BottomSheetProps
} from '../bottom-sheet/bottom-sheet';
import CheckIcon from '@tradera/blueprint/theme/icons2/check.svg?jsx';
import useIsDesktop from 'tradera/hooks/use-is-desktop';
import { useIsTablet } from 'tradera/hooks/use-is-tablet';
import { useEventListener } from 'tradera/hooks/use-event-listener';
import { useClickOutside } from 'tradera/hooks/use-click-outside';
import { composeEventHandlers } from 'tradera/utils/compose-event-handler';
import { ScrollableModalContent } from '../scrollable-modal/scrollable-modal-content';
import { type ScrollableModalContentProps } from '../scrollable-modal/scrollable-modal-content';
import { classNames } from 'tradera/utils/class-names';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import ALink from '../alink/alink';
import { mergeRefs } from 'tradera/utils/merge-refs';
import type { RhythmScale } from 'tradera/components/list/list';

type TDropdownMenuContext = {
    isOpen: boolean;
    toggleOpen: () => void;
    open: () => void;
    close: () => void;
    triggerRef: RefObject<HTMLElement> | null;
    setTriggerRef: (ref: RefObject<HTMLElement> | null) => void;
    closeMenuOnSelect: boolean;
    setCloseMenuOnSelect: (close: boolean) => void;
    triggerWidth?: number;
    setTriggerWidth: (width?: number) => void;
};

const DropdownMenuContext = createContext<TDropdownMenuContext | undefined>(
    undefined
);

export const useDropdownMenuContext = () => {
    const context = useContext(DropdownMenuContext);

    if (!context) {
        throw new Error(
            'DropdownMenu components cannot be used outside of DropdownMenuContext'
        );
    }
    return context;
};

type DropdownMenuProps = {
    className?: string;
    children: ReactNode;
    onOpenChange?: (isOpen: boolean) => void;
};

export const DropdownMenu = ({
    className,
    children,
    onOpenChange
}: DropdownMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [triggerRef, setTriggerRef] = useState<RefObject<HTMLElement> | null>(
        null
    );
    const [closeMenuOnSelect, setCloseMenuOnSelect] = useState(false);
    const [triggerWidth, setTriggerWidth] = useState<number | undefined>();

    const handleToggleOpen = (shouldBeOpenProp?: boolean) => {
        // Don't do anything if already opened / closed
        if (isOpen === shouldBeOpenProp) return;

        const shouldBeOpen =
            typeof shouldBeOpenProp !== 'boolean' ? !isOpen : shouldBeOpenProp;

        setIsOpen(shouldBeOpen);
        onOpenChange?.(shouldBeOpen);

        triggerRef?.current?.focus();
    };

    return (
        <DropdownMenuContext.Provider
            value={{
                isOpen,
                triggerRef,
                setTriggerRef,
                toggleOpen: handleToggleOpen,
                open: () => handleToggleOpen(true),
                close: () => handleToggleOpen(false),
                closeMenuOnSelect,
                setCloseMenuOnSelect,
                triggerWidth,
                setTriggerWidth
            }}>
            <div className={classNames('position-relative', className)}>
                {children}
            </div>
        </DropdownMenuContext.Provider>
    );
};

export type DropdownMenuContentProps = {
    children: React.ReactNode;
    /**
     * A title to display in the bottom sheet for mobile
     */
    title?: string;
    /**
     * Display a footer in the bottom sheet for mobile
     */
    footer?: ScrollableModalContentProps['footer'];
    /**
     * If true, the menu will close when an item is selected.
     * Defaults to true.
     */
    closeMenuOnSelect?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    /**
     * If true, the menu will be sized to the trigger element.
     */
    sizeToTrigger?: boolean;
    size?: number | string;
    position?: 'left' | 'right';
    scrollable?: boolean;
    maxHeight?: number | string;
    dynamicHeight?: boolean;
    className?: string;
    paddingTop?: boolean;
    paddingBottom?: boolean;
    zIndex?: number;
} & Pick<BottomSheetProps, 'bottomAnchor'>;

export const DropdownMenuContent = forwardRef<
    HTMLDivElement,
    DropdownMenuContentProps
>(function DropdownMenuContent(
    {
        children,
        bottomAnchor,
        title,
        footer,
        onKeyDown,
        onClick,
        closeMenuOnSelect,
        sizeToTrigger,
        size,
        maxHeight,
        position = 'left',
        scrollable = true,
        dynamicHeight = true,
        className,
        paddingTop,
        paddingBottom,
        zIndex
    },
    forwardedRef
) {
    const { isOpen, close, triggerRef, setCloseMenuOnSelect, triggerWidth } =
        useDropdownMenuContext();
    const timerRef = useRef(0);
    const searchRef = useRef('');
    const ref = useRef<HTMLDivElement>(null);

    const isTablet = useIsTablet();
    const isDesktop = useIsDesktop();

    const isLargeScreen = isTablet || isDesktop;

    useClickOutside(ref, close, triggerRef);

    useEffect(() => {
        setCloseMenuOnSelect(closeMenuOnSelect ?? true);
    }, [closeMenuOnSelect, setCloseMenuOnSelect]);

    const getMenuItems = () => {
        if (!ref.current) return null;
        const focusableElements = ref.current.querySelectorAll(
            '[role="menuitem"]:not([disabled])'
        );
        const focusableElementArray = Array.from(focusableElements);
        return focusableElementArray;
    };

    // focus on content element when opened
    // this is a poor-man's focus trap
    useEffect(() => {
        if (isOpen && ref.current) {
            ref.current?.focus({ preventScroll: true });
        }
    }, [isOpen]);

    // find next focusable element, this is what we use to navigate with arrow keys
    const findNextFocusableElement = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (
                ref.current &&
                event.key !== 'ArrowDown' &&
                event.key !== 'ArrowUp'
            )
                return;

            const focusableElements = getMenuItems();
            if (!focusableElements) {
                return;
            }
            const currentFocusedIndex = focusableElements.indexOf(
                event.target as HTMLElement
            );
            const currentFocusableElement =
                focusableElements[currentFocusedIndex];
            const nextFocusableElement =
                focusableElements[
                    currentFocusedIndex + (event.key === 'ArrowDown' ? 1 : -1)
                ];

            if (!nextFocusableElement) {
                event.preventDefault();
                return;
            }

            (nextFocusableElement as HTMLElement)?.focus();
            (nextFocusableElement as HTMLElement)?.setAttribute(
                'tabIndex',
                '0'
            );
            (currentFocusableElement as HTMLElement)?.setAttribute(
                'tabIndex',
                '-1'
            );
            event.preventDefault();
        },
        [ref]
    );

    // From https://github.com/radix-ui/primitives/blob/main/packages/react/menu/src/Menu.tsx
    /**
     * Wraps an array around itself at a given start index
     * Example: `wrapArray(['a', 'b', 'c', 'd'], 2) === ['c', 'd', 'a', 'b']`
     */
    function wrapArray<T>(array: T[], startIndex: number) {
        return array.map(
            (_, index) => array[(startIndex + index) % array.length]
        );
    }
    /**
     * This is the "meat" of the typeahead matching logic. It takes in all the values,
     * the search and the current match, and returns the next match (or `undefined`).
     *
     * We normalize the search because if a user has repeatedly pressed a character,
     * we want the exact same behavior as if we only had that one character
     * (ie. cycle through options starting with that character)
     *
     * We also reorder the values by wrapping the array around the current match.
     * This is so we always look forward from the current match, and picking the first
     * match will always be the correct one.
     *
     * Finally, if the normalized search is exactly one character, we exclude the
     * current match from the values because otherwise it would be the first to match always
     * and focus would never move. This is as opposed to the regular case, where we
     * don't want focus to move if the current match still matches.
     */
    function getNextMatch(
        values: string[],
        search: string,
        currentMatch?: string
    ) {
        const isRepeated =
            search.length > 1 &&
            Array.from(search).every((char) => char === search[0]);
        const normalizedSearch = isRepeated ? search[0] : search;
        const currentMatchIndex = currentMatch
            ? values.indexOf(currentMatch)
            : -1;
        let wrappedValues = wrapArray(values, Math.max(currentMatchIndex, 0));
        const excludeCurrentMatch = normalizedSearch.length === 1;
        if (excludeCurrentMatch)
            wrappedValues = wrappedValues.filter((v) => v !== currentMatch);
        const nextMatch = wrappedValues.find((value) =>
            value.toLowerCase().startsWith(normalizedSearch.toLowerCase())
        );
        return nextMatch !== currentMatch ? nextMatch : undefined;
    }

    // From https://github.com/radix-ui/primitives/blob/main/packages/react/menu/src/Menu.tsx
    const handleTypeaheadSearch = (key: string) => {
        const search = searchRef.current + key;
        const items = getMenuItems() as HTMLDivElement[] | null;

        if (!items) {
            return;
        }

        const currentItem = document.activeElement;

        const currentMatch = items?.find(
            (item) => item === currentItem
        )?.innerText;
        const values = items.map((item) => item.innerText);
        const nextMatch = getNextMatch(values, search, currentMatch);
        const newItem = items.find((item) => item.innerText === nextMatch);

        // Reset `searchRef` 1 second after it was last updated
        (function updateSearch(value: string) {
            searchRef.current = value;
            window.clearTimeout(timerRef.current);
            if (value !== '')
                timerRef.current = window.setTimeout(
                    () => updateSearch(''),
                    1000
                );
        })(search);

        if (newItem) {
            /**
             * Imperative focus during keydown is risky so we prevent React's batching updates
             * to avoid potential bugs. See: https://github.com/facebook/react/issues/20332
             */
            setTimeout(() => (newItem as HTMLElement).focus());
        }
    };

    const lockTab = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab') {
            // do not steal focus if there are other following items in the menu that can be tabbed to
            const tabbableElements = ref.current?.querySelectorAll(
                'input, button, [role="menuitem"][tabindex="0"]'
            );
            const tabbableElementsArray = tabbableElements
                ? Array.from(tabbableElements)
                : [];
            const lastTabbableElement =
                tabbableElementsArray[tabbableElementsArray.length - 1];
            const firstTabbableElement = tabbableElementsArray[0];

            if (
                (document.activeElement === firstTabbableElement ||
                    isSameInputRadioGrouping(
                        document.activeElement,
                        firstTabbableElement
                    )) &&
                event.shiftKey
            ) {
                (lastTabbableElement as HTMLElement)?.focus();
                event.preventDefault();
                return;
            }

            if (
                (document.activeElement === lastTabbableElement ||
                    isSameInputRadioGrouping(
                        document.activeElement,
                        lastTabbableElement
                    )) &&
                !event.shiftKey
            ) {
                (firstTabbableElement as HTMLElement)?.focus();
                event.preventDefault();
                return;
            }
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        findNextFocusableElement(event);
        lockTab(event);

        /**
         * Stolen from https://github.com/radix-ui/primitives/blob/main/packages/react/menu/src/Menu.tsx
         * Do not perform typeahead when the user is navigating event.key is ArrowUp/Down/Left/Right
         * which we would not like to send as a search character to `handleTypeaheadSearch`
         **/
        const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
        const isCharacterKey = event.key.length === 1;
        if (!isModifierKey && isCharacterKey) handleTypeaheadSearch(event.key);
    };

    return (
        <>
            {isLargeScreen ? (
                <>
                    {isOpen && (
                        <div
                            // We want this to act as a menu but also not be tabbed to, so -1
                            role="menu"
                            tabIndex={-1}
                            onKeyDown={(event) =>
                                composeEventHandlers(
                                    onKeyDown,
                                    handleOnKeyDown
                                )(event)
                            }
                            onClick={onClick}
                            className={classNames(
                                styles.content,
                                position === 'right' && styles.right,
                                scrollable && styles.scrollable,
                                className
                            )}
                            style={{
                                maxHeight: maxHeight,
                                width: sizeToTrigger
                                    ? triggerWidth
                                    : size !== undefined
                                      ? size
                                      : 'auto',
                                minWidth:
                                    sizeToTrigger || size !== undefined
                                        ? 0
                                        : undefined,
                                zIndex: zIndex
                            }}
                            ref={mergeRefs(forwardedRef, ref)}>
                            {children}
                        </div>
                    )}
                </>
            ) : (
                <BottomSheet
                    show={isOpen}
                    onDismiss={close}
                    maxHeight={maxHeight}
                    bottomAnchor={bottomAnchor}
                    className={classNames(
                        'pb-5',
                        dynamicHeight ? 'h-auto' : 'h-100',
                        className
                    )}>
                    <ScrollableModalContent
                        title={title ?? ''}
                        footer={footer}
                        onClose={close}
                        paddingX={false} // this is to match the gutters
                        paddingTop={paddingTop}
                        paddingBottom={paddingBottom}>
                        {children}
                    </ScrollableModalContent>
                </BottomSheet>
            )}
        </>
    );
});

export type DropdownMenuItemProps = {
    icon?: React.ReactNode;
    className?: string;
    asChild?: boolean;
    children: React.ReactNode;
    disabled?: boolean;
    isSelected?: boolean;
    onSelect?: (
        event:
            | React.MouseEvent<HTMLDivElement>
            | React.KeyboardEvent<HTMLDivElement>
            | TouchEvent
    ) => void;
    onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
    href?: string;
    useLink?: boolean;
    /**
     * Defaults to true
     */
    separator?: boolean;
    /**
     * Adds padding to the left and right of the item so that it is selectable
     * 100% width, but the text and separator are padded.
     * Default is "base".
     *
     * This mimics the gutters prop in List
     */
    gutters?: RhythmScale;
};

/**
 * This is the primitive component used to render a dropdown menu item.
 * Its job is just to forward props to the underlying DOM element.
 */
const DropdownMenuItemPrimitive = ({
    children,
    onFocus,
    onSelect
}: Pick<DropdownMenuItemProps, 'children' | 'onFocus' | 'onSelect'>) => {
    const { closeMenuOnSelect, close } = useDropdownMenuContext();

    const handleOnSelect = (event: React.MouseEvent<HTMLDivElement>) => {
        composeEventHandlers(onSelect, () => {
            if (closeMenuOnSelect) {
                close();
            }
        })(event);
    };

    const childrenArray = Children.toArray(children);

    const enhancedChildren = childrenArray.map((child) => {
        const props = getPropsFromReactElement(child);
        return isValidElement(child) &&
            props.role === 'menuitem' &&
            !props.disabled
            ? cloneElement(child as React.ReactElement, {
                  onClick: (e: React.MouseEvent<HTMLDivElement>) =>
                      composeEventHandlers(props.onClick, handleOnSelect)(e),
                  onFocus,
                  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === 'Enter') {
                          composeEventHandlers(onSelect, (e) => {
                              if (closeMenuOnSelect) {
                                  close();
                                  // prevent default to not make the trigger open the menu again
                                  e.preventDefault();
                              }
                          })(event);
                      }
                  },
                  tabIndex: -1,
                  role: 'menuitem'
              })
            : child;
    });

    return <>{enhancedChildren}</>;
};

export const DropdownMenuItem = ({
    icon,
    className,
    href,
    useLink,
    asChild,
    onSelect,
    onFocus,
    isSelected,
    separator = true,
    gutters = 'base',
    ...props
}: DropdownMenuItemProps) => {
    if (asChild) {
        if (href) throw new Error('Cannot render href and asChild.');
        return (
            <DropdownMenuItemPrimitive
                onSelect={onSelect}
                onFocus={onFocus}
                {...props}
            />
        );
    }

    return (
        <DropdownMenuItemPrimitive onSelect={onSelect} onFocus={onFocus}>
            {href ? (
                <ALink
                    className={classNames(
                        styles.item,
                        { disabled: props.disabled },
                        className
                    )}
                    data-gutters={gutters}
                    href={href}
                    useLink={useLink}>
                    {icon}
                    {props.children}
                </ALink>
            ) : (
                <div
                    className={classNames(
                        styles.item,
                        { disabled: props.disabled },
                        className
                    )}
                    data-gutters={gutters}
                    tabIndex={-1}
                    role="menuitem"
                    {...props}>
                    {icon}
                    {props.children}
                    {isSelected && (
                        <span className="ml-auto d-flex">
                            <CheckIcon className="ml-1 icon" />
                        </span>
                    )}
                </div>
            )}
            {separator && (
                <Divider
                    as="div"
                    className={classNames(styles.separator, className)}
                    data-gutters={gutters}
                />
            )}
        </DropdownMenuItemPrimitive>
    );
};

const getPropsFromReactElement = (element: React.ReactNode) => {
    const props = isValidElement(element) ? element.props : undefined;
    if (!props) return props;
    return typeof props === 'object' && props ? props : undefined;
};

/**
 * This is a wrapper around the trigger element that handles opening and closing the menu, its
 * main purpose if to forward event handlers to the trigger element.
 */
const DropdownMenuPrimitiveTrigger = forwardRef<
    HTMLSpanElement,
    DropdownButtonProps
>(({ children, disabled, ...otherProps }, forwardedRef) => {
    const { toggleOpen, open, isOpen } = useDropdownMenuContext();

    const childrenArray = Children.toArray(children);

    const enhancedChildren = childrenArray.map((child) => {
        const props = getPropsFromReactElement(child);
        return isValidElement(child)
            ? cloneElement(child as React.ReactElement, {
                  ...otherProps,
                  onClick: (e: React.MouseEvent) =>
                      composeEventHandlers(props.onClick, toggleOpen)(e),
                  /**
                   *  This makes sure that we can open a menu with the keyboard just like a native <select> element
                   */
                  onKeyDown: (e: React.KeyboardEvent) =>
                      composeEventHandlers(
                          props.onKeyDown,
                          (event: React.KeyboardEvent) => {
                              if (disabled) return;
                              if (['Enter', ' '].includes(event.key))
                                  toggleOpen();
                              if (event.key === 'ArrowDown') open();
                              // prevent keydown from scrolling window / first focused item to execute
                              // that keydown (inadvertently closing the menu)
                              if (
                                  ['Enter', ' ', 'ArrowDown'].includes(
                                      event.key
                                  )
                              ) {
                                  event.preventDefault();
                              }
                          }
                      )(e),
                  ref: forwardedRef,
                  'data-state': isOpen ? 'open' : 'closed'
              })
            : child;
    });
    return <>{enhancedChildren}</>;
});

DropdownMenuPrimitiveTrigger.displayName = 'DropdownMenuPrimitiveTrigger';

/**
 * This is used to determine if both element are grouped as radio buttons.
 * Only one of the radios will be directly accessible with tab and therefor we need to
 * now this to determin if the selected element is the first or last tabbable element.
 */
const isSameInputRadioGrouping = (el1: Element | null, el2: Element | null) =>
    el1 &&
    el2 &&
    el1.tagName === 'INPUT' &&
    el2.tagName === 'INPUT' &&
    el1.getAttribute('type') === 'radio' &&
    el2.getAttribute('type') === 'radio' &&
    el1.getAttribute('name') === el2.getAttribute('name');

export type DropdownMenuTriggerProps = DropdownButtonProps & {
    asChild?: boolean;
    onPointerOver?: (event: React.PointerEvent<HTMLButtonElement>) => void;
    nonBrandedText?: boolean;
};

/**
 * This is what opens the menu, it can be a button, a link, or any other element.
 * If no child is specified, it will render a button with a down arrow (DropdownButton).
 */
export const DropdownMenuTrigger = ({
    asChild,
    nonBrandedText,
    ...props
}: DropdownMenuTriggerProps) => {
    const { setTriggerRef, isOpen, setTriggerWidth, toggleOpen } =
        useDropdownMenuContext();
    const ref = useRef<HTMLElement>(null);

    const handleWindowResize = () => {
        const el = ref.current;

        if (!el) {
            return;
        }
        setTriggerWidth(el?.offsetWidth);
    };

    useEffect(() => {
        if (ref && 'current' in ref) {
            setTriggerRef(ref);
            setTriggerWidth(ref.current?.offsetWidth);
        }
    }, [ref, setTriggerRef, setTriggerWidth]);

    useEventListener('resize', handleWindowResize);

    if (asChild) {
        return <DropdownMenuPrimitiveTrigger ref={ref} {...props} />;
    }
    return (
        <DropdownMenuPrimitiveTrigger
            ref={ref}
            disabled={props.disabled}
            onKeyDown={props.onKeyDown}>
            <DropdownButton
                nonBrandedText={nonBrandedText}
                isOpen={isOpen}
                onClick={toggleOpen}
                {...props}
            />
        </DropdownMenuPrimitiveTrigger>
    );
};

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

type DropdownIconProps = { className?: string };
export const DropdownIcon = ({ className }: DropdownIconProps) => {
    const { isOpen } = useDropdownMenuContext();
    return (
        <ArrowDownIcon
            className={classNames(isOpen && styles.dropdownIconOpen, className)}
        />
    );
};
